import {
  InitOptions,
  Control,
  ControlsPosition,
  Controls as ControlType,
  ProgressBar,
  SpecificDevice,
  SpecificDevices,
  Responsive,
  BigButton,
  ControlComponentItem,
  ControlsSettings,
  PluginControlPosition,
  QualityLevelKind,
} from '../../types/player';
import Sdk from '../Sdk';
import PluginInterface from '../plugins/PluginInterface';
import PluginsManager from '../plugins/PluginsManager';

class Controls {
  private controls!: ControlType;
  private controlsDefault!: ControlType;
  private pluginControls: {
    [key in ControlsPosition]?: ControlComponentItem[];
  } = {};
  private sdk: Sdk;
  private justShowList: Control[] = [];
  private _muteButtonShrinked: boolean = false;

  constructor(sdk: Sdk) {
    this.sdk = sdk;
  }

  manageSpecificDevice(specificDevices: SpecificDevices) {
    let currentSize = this.sdk.getSize();
    let currentWidth =
      this.sdk
        .options()
        .getResponsive()
        ?.find((size) => size.className === currentSize)?.width || 0;

    let isSpecificDevice;

    let mergedControls = JSON.parse(JSON.stringify(this.controlsDefault));

    for (var i in specificDevices) {
      let specificDevice: SpecificDevice = specificDevices[i];
      let specificDeviceInfo = specificDevice.device;
      let deviceInfo = this.sdk.getUserAgent().getInfo();
      isSpecificDevice = true;

      for (var i in specificDeviceInfo) {
        if (deviceInfo[i] != specificDeviceInfo[i]) {
          isSpecificDevice = false;
          break;
        }
      }

      if (isSpecificDevice && specificDevice.size) {
        let specificWidth = this.sdk
          .options()
          .getResponsive()
          ?.find((size) => size.className === specificDevice.size)?.width;
        if (!currentWidth) isSpecificDevice = false;
        else if (specificWidth && currentWidth > specificWidth) {
          isSpecificDevice = false;
        }
      }

      if (isSpecificDevice) {
        mergedControls = this.sdk
          .options()
          .merge(
            JSON.parse(JSON.stringify(mergedControls)),
            specificDevice.controls,
          );
      }
    }
    if (this.controls && this.controls.hideList) {
      mergedControls.hideList = Array.from(
        new Set([
          ...(mergedControls.hideList || []),
          ...this.controls.hideList,
        ]),
      );
    }
    this.controls = mergedControls;
  }

  getData() {
    return this.controls;
  }

  initControls(controls: ControlType) {
    this.controls = JSON.parse(JSON.stringify(controls));
    this.controlsDefault = JSON.parse(JSON.stringify(controls));
    this.manageSpecificDevice(this.controls.specificDevices || []);
    this.manageSubMenu();
  }

  manageSubMenu() {
    if (this.controls.subMenu && this.controls.subMenu?.length > 0) {
      //remove controls contain in submenu from other positions
      let subMenuControls: Control[] = this.getSubMenuControls();
      let positions: ControlsPosition[] = this.getPositions();
      for (const position of positions) {
        for (const control of subMenuControls) {
          let controls: Control[] = this.controls[position] as Control[];
          if (controls) {
            this.controls[position as any as Control] = controls.filter(
              (c: Control) => {
                return c !== control;
              },
            );
          }
        }
      }
    }
  }

  getSubMenuControls(): Control[] {
    let controls: Control[] = [];
    if (!this.controls.subMenu) return controls;
    for (const control of this.controls.subMenu) {
      //convert controlSubMenu type to Control type
      controls.push(control as any as Control);
    }
    return controls;
  }

  refresh() {
    this.initControls(this.controlsDefault);
  }

  isShow(): boolean {
    return this.controls.show !== false;
  }

  getSettings(): ControlsSettings {
    return this.controls.settings as ControlsSettings;
  }

  getProgressBar(): ProgressBar {
    return this.controls.progressBar!;
  }

  getLiveAutoHide(): Control[] | undefined {
    return this.controls.liveAutoHide;
  }

  getBigButton(): BigButton | undefined {
    return this.controls.bigButton;
  }

  setJustShowList(justShowList: Control[]) {
    this.justShowList = justShowList;
  }

  isMuteButtonShrinked(): boolean {
    return this._muteButtonShrinked;
  }

  setMuteButtonShrinked(value: boolean) {
    this._muteButtonShrinked = value;
  }

  list(position: ControlsPosition): ControlComponentItem[] {
    let controls: Control[] = this._list(position);
    let item: ControlComponentItem;
    let list: ControlComponentItem[] = controls.map((control: Control) => {
      item = {
        name: control,
      };
      return item;
    });

    if (this.justShowList.length > 0) {
      list = list.filter((control: ControlComponentItem) => {
        return this.justShowList.includes(control.name as Control);
      });
    }

    let listOfPluginControls: ControlComponentItem[] =
      this.pluginControls[position] === undefined
        ? []
        : (this.pluginControls[position] as ControlComponentItem[]);
    for (var i in listOfPluginControls) {
      let pluginControl: ControlComponentItem = listOfPluginControls[i];
      //sort plugin controls with it position
      let position: PluginControlPosition =
        pluginControl.position as PluginControlPosition;
      if (position.index === 0) list.unshift(pluginControl);
      else if (position.index === -1) list.push(pluginControl);
      else {
        position.index = position.index ? (position.index as number) : 0;
        list.splice(position.index, 0, pluginControl);
      }
    }
    return list;
  }

  retrieveControl(control: Control) {
    //check for all controls in all positions
    let positions = this.getPositions();

    for (const position of positions) {
      let controls: Control[] = this.controls[position] as Control[];
      if (controls && controls.includes(control)) return control;
    }

    return false;
  }

  getPositions() {
    return [
      ControlsPosition.TOP_LEFT,
      ControlsPosition.TOP_RIGHT,
      ControlsPosition.BOTTOM_LEFT,
      ControlsPosition.BOTTOM_RIGHT,
      ControlsPosition.MIDDLE,
    ];
  }

  _list(position: ControlsPosition): Control[] {
    if (!this.controls) return [];
    let controlList = this.controls[position] as Control[];
    if (!controlList) return [];
    return controlList.filter((control: Control) => {
      if (this.controls?.hideList && this.controls.hideList.includes(control))
        return false;
      if (this.sdk.isLive() && this.controls.liveAutoHide?.includes(control))
        return false;
      switch (control) {
        case 'qualityLevels_selector':
          return (
            this.sdk.hasQualityLevels(QualityLevelKind.AUDIO) ||
            this.sdk.hasQualityLevels(QualityLevelKind.VIDEO)
          );
        case 'audiotracks_selector':
          return this.sdk.hasAudioTracks();
        case 'texttracks_selector':
          return this.sdk.hasTextTracks();
        case 'videotracks_selector':
          return this.sdk.hasVideoTracks();
        case 'playbackRates_selector':
          return this.sdk.hasPlaybackRates();
      }
      return true;
    });
  }

  addPluginControl(
    pluginName: string,
    controlName: string,
    pluginComponentPosition: PluginControlPosition,
    plugin: PluginInterface,
  ) {
    let position: ControlsPosition = pluginComponentPosition.position;
    if (!this.pluginControls[position]) {
      this.pluginControls[position] = [];
    }

    this.pluginControls[position]?.push({
      name: Control.PLUGIN_SKELETON,
      pluginName: pluginName,
      controlName: controlName,
      position: pluginComponentPosition,
      plugin,
    });
  }
}

export default Controls;
